import { Platform } from 'react-native';
import { supabase } from '../lib/supabase';

export class BadgeService {
  private static instance: BadgeService;
  private lastUpdateTime = 0;
  private lastBadgeCount = -1;
  private updateInProgress = false;

  static getInstance(): BadgeService {
    if (!BadgeService.instance) {
      BadgeService.instance = new BadgeService();
    }
    return BadgeService.instance;
  }

  /**
   * Update badge count with debouncing and error handling
   */
  async updateBadgeCount(count: number): Promise<boolean> {
    // Debounce badge updates to prevent excessive calls
    const now = Date.now();
    const minUpdateInterval = 1000; // Minimum 1 second between updates

    if (now - this.lastUpdateTime < minUpdateInterval && this.lastBadgeCount === count) {
      console.log('📱 Badge update debounced - same count too soon:', count);
      return true;
    }

    // Prevent concurrent updates
    if (this.updateInProgress) {
      console.log('📱 Badge update already in progress, skipping');
      return false;
    }

    try {
      this.updateInProgress = true;
      this.lastUpdateTime = now;

      // Skip badge updates on web - not supported
      if (Platform.OS === 'web') {
        console.log('📱 Web environment - badge updates not supported, using document title fallback');
        return this.updateWebDocumentTitle(count);
      }

      console.log('📱 Updating badge count to:', count);

      // Try Expo notifications first (most reliable on native)
      const expoSuccess = await this.updateBadgeViaExpoNotifications(count);
      if (expoSuccess) {
        this.lastBadgeCount = count;
        console.log('✅ Badge updated via Expo Notifications:', count);
        return true;
      }

      // Fallback to OneSignal if available
      const oneSignalSuccess = await this.updateBadgeViaOneSignal(count);
      if (oneSignalSuccess) {
        this.lastBadgeCount = count;
        console.log('✅ Badge updated via OneSignal:', count);
        return true;
      }

      // If both methods fail, log the issue but don't throw
      console.warn('⚠️ All badge update methods failed for count:', count);
      return false;

    } catch (error: any) {
      console.error('❌ Badge update failed with exception:', {
        count,
        error: error?.message || 'Unknown error',
        platform: Platform.OS
      });
      return false;
    } finally {
      this.updateInProgress = false;
    }
  }

  /**
   * Try updating badge via Expo Notifications
   */
  private async updateBadgeViaExpoNotifications(count: number): Promise<boolean> {
    try {
      // Early exit for web platform
      if (Platform.OS === 'web') {
        console.log('ℹ️ Web platform - native notifications not available');
        return false;
      }

      // Check execution environment to avoid native module errors
      const Constants = await import('expo-constants');
      const isExpoGo = Constants.default.executionEnvironment === 'storeClient';

      // More comprehensive environment checking
      if (isExpoGo) {
        console.log('ℹ️ Expo Go environment - native notifications not available');
        return false;
      }

      // Check if we're in a web context (including Metro web)
      if (typeof window !== 'undefined' && typeof document !== 'undefined') {
        console.log('ℹ️ Web context detected - native notifications not available');
        return false;
      }

      // Try to import but handle import errors
      let ExpoNotifications;
      try {
        ExpoNotifications = await import('expo-notifications');
      } catch (importError: any) {
        console.log('ℹ️ expo-notifications module not available:', importError?.message);
        return false;
      }

      if (ExpoNotifications.setBadgeCountAsync) {
        await ExpoNotifications.setBadgeCountAsync(count);
        return true;
      } else {
        console.log('ℹ️ setBadgeCountAsync not available in Expo Notifications');
        return false;
      }
    } catch (error: any) {
      // Handle specific native module errors gracefully
      if (error?.message?.includes('ExpoPushTokenManager') || 
          error?.message?.includes('native module') ||
          error?.message?.includes('NativeModule') ||
          error?.message?.includes('Cannot find native module')) {
        console.log('ℹ️ Native notification modules not available in current environment');
        return false;
      }

      console.log('ℹ️ Expo notifications not available:', error?.message || 'Unknown error');
      return false;
    }
  }

  /**
   * Try updating badge via OneSignal
   */
  private async updateBadgeViaOneSignal(count: number): Promise<boolean> {
    try {
      const OneSignal = await import('react-native-onesignal');

      if ((OneSignal as any).default?.User?.pushSubscription?.setBadgeCount) {
        await (OneSignal as any).default.User.pushSubscription.setBadgeCount(count);
        return true;
      } else {
        console.log('ℹ️ OneSignal badge method not available');
        return false;
      }
    } catch (error: any) {
      console.log('ℹ️ OneSignal not available:', error?.message || 'Unknown error');
      return false;
    }
  }

  /**
   * Web fallback - update document title to show unread count
   */
  private updateWebDocumentTitle(count: number): boolean {
    try {
      if (typeof document !== 'undefined') {
        const baseTitle = 'Padrinho';
        document.title = count > 0 ? `(${count}) ${baseTitle}` : baseTitle;
        console.log('📱 Web document title updated:', document.title);
        return true;
      }
      return false;
    } catch (error: any) {
      console.log('ℹ️ Could not update document title:', error?.message);
      return false;
    }
  }

  /**
   * Clear badge count
   */
  async clearBadge(): Promise<boolean> {
    console.log('🧹 Clearing badge count');
    return this.updateBadgeCount(0);
  }

  /**
   * Get the last known badge count
   */
  getLastBadgeCount(): number {
    return this.lastBadgeCount;
  }

  /**
   * Force update badge count (bypasses debouncing)
   */
  async forceUpdateBadgeCount(count: number): Promise<boolean> {
    this.lastUpdateTime = 0; // Reset debounce timer
    this.lastBadgeCount = -1; // Reset cached count
    return this.updateBadgeCount(count);
  }

  /**
   * Static method to clear badge count
   */
  static async clearBadge(): Promise<boolean> {
    const instance = BadgeService.getInstance();
    return instance.clearBadge();
  }

  /**
   * Static method to set badge count
   */
  static async setBadgeCount(count: number): Promise<boolean> {
    const instance = BadgeService.getInstance();
    return instance.updateBadgeCount(count);
  }

  /**
   * Sync badge with current notification unread count
   */
  static async syncBadgeWithNotifications(): Promise<boolean> {
    try {
      // Use NotificationCacheService for faster badge updates
      const { default: NotificationCacheService } = await import('./notificationCacheService');
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        await BadgeService.clearBadge();
        return true;
      }

      const result = await NotificationCacheService.getNotifications(user.id);
      const unreadCount = result.unread_count || 0;

      await BadgeService.setBadgeCount(unreadCount);
      console.log('📱 Badge synced with cached notification count:', unreadCount);
      return true;
    } catch (error) {
      console.error('❌ Badge sync with notifications failed:', error);
      // Fallback to direct service call
      try {
        const { NotificationsService } = await import('./notificationsService');
        const result = await NotificationsService.getUnreadCount();

        if (result.success && typeof result.count === 'number') {
          await BadgeService.setBadgeCount(result.count);
        } else {
          await BadgeService.clearBadge();
        }
        return true;
      } catch (fallbackError) {
        console.error('❌ Badge sync fallback also failed:', fallbackError);
        await BadgeService.clearBadge();
        return false;
      }
    }
  }

  /**
   * Check if badge updates are supported on current platform
   */
  isBadgeSupported(): boolean {
    return Platform.OS !== 'web';
  }

  /**
   * Get platform-specific badge guidance
   */
  getBadgeGuidance(): string {
    if (Platform.OS === 'web') {
      return 'Badge counts shown in browser tab title';
    } else if (Platform.OS === 'ios') {
      return 'Badge counts shown on iOS app icon';
    } else if (Platform.OS === 'android') {
      return 'Badge counts may require launcher support on Android';
    } else {
      return 'Badge support varies by platform';
    }
  }
}

// Export singleton instance
export default BadgeService.getInstance();